import React from "react";
import { ChemicalRecord, MainProcessConfig, TransportRoute, WasteRecord } from "../../../../../types/process/process.types";
import { ApiProcessingRecord, CipSipFormValues, CipSipRecord } from "../../../../../types/prodcut-data-review/product-data.types";
import { MASS_UNIT_MAP } from "../utils/processDetail.utils";
import ChemicalSection from "../../chemicalRecords/ChemicalSection";
import CipSipSection from "./CipSipSection";
import RecoverySection from "./RecoverySection";
import WasteSection from "../../wasteRecords/WasteSection";

interface ProcessSubsectionsProps {
  process: ApiProcessingRecord;
  gateKind: "farm" | "process";
  disabled?: boolean;
  chemicals: ChemicalRecord[];
  wastes: WasteRecord[];
  cipSipRecords: CipSipRecord[];
  onAddChemical: (initial?: Partial<ChemicalRecord>) => void;
  onUpdateChemical: (id: string, patch: Partial<ChemicalRecord>) => void;
  onRemoveChemical: (id: string) => void;
  onAddRoute: (chemId: string, route: Pick<TransportRoute, "type" | "distance">) => void;
  onUpdateRoute: (chemId: string, routeId: string, patch: Partial<TransportRoute>) => void;
  onRemoveRoute: (chemId: string, routeId: string) => void;
  onAddWaste: (initial?: Partial<WasteRecord>) => void;
  onUpdateWaste: (id: string, patch: Partial<WasteRecord>) => void;
  onRemoveWaste: (id: string) => void;
  onAddCip: (values: CipSipFormValues) => void;
  onUpdateCip: (id: string, patch: CipSipFormValues) => void;
  onRemoveCip: (id: string) => void;
}

export const ProcessSubsections: React.FC<ProcessSubsectionsProps> = ({
  process,
  gateKind,
  disabled = false,
  chemicals,
  wastes,
  cipSipRecords,
  onAddChemical,
  onUpdateChemical,
  onRemoveChemical,
  onAddRoute,
  onUpdateRoute,
  onRemoveRoute,
  onAddWaste,
  onUpdateWaste,
  onRemoveWaste,
  onAddCip,
  onUpdateCip,
  onRemoveCip
}) => {
  // Derive a read-only MainProcessConfig for RecoverySection from API fields.
  // The API has no recovery fields — updateMain is a no-op.
  const mainConfig: MainProcessConfig = {
    processingName: process.processingName ?? "",
    dataType: "",
    processingType: "" as any,
    facilityName:
      typeof process.facilityId === "object" && process.facilityId !== null
        ? (process.facilityId as any).facilityName ?? ""
        : "",
    machineModelNumber: process.machineModelNo ?? "",
    capacity: process.capacity,
    capacityUnit: MASS_UNIT_MAP[process.capacityUnit] ?? "kg",
    input: process.input,
    inputUnit: MASS_UNIT_MAP[process.inputUnit] ?? "kg",
    output: process.output,
    outputUnit: MASS_UNIT_MAP[process.outputUnit] ?? "kg",
    wasteSideStream: process.sideStream,
    wasteSideStreamUnit: MASS_UNIT_MAP[process.sideStreamUnit] ?? "kg",
    electricity: process.electricity,
    electricityUnit: "" as any,
    water: process.water,
    waterUnit: "" as any,
    heat: process.heat,
    heatUnit: "" as any,
    heatSources: [],
    waterSources: [],
    isRecoveringWater: false,
    recoveredWaterAmount: "",
    recoveredWaterUnit: "L",
    isRecoveringHeat: false,
    recoveredHeatAmount: "",
    recoveredHeatUnit: "MJ/kg",
    isSaved: true
  };

  // Recovery – no API field; updateMain is a no-op.
  const updateMain = (_patch: Partial<MainProcessConfig>) => {};

  return (
    <div className="tw-space-y-3">
      <div className="tw-text-[11px] tw-font-semibold tw-text-muted-foreground tw-uppercase tw-tracking-wide tw-pl-1">
        {process.processingName || "Process"} — sub-sections
      </div>
      <ChemicalSection
        records={chemicals}
        disabled={disabled}
        onAdd={onAddChemical}
        onUpdate={onUpdateChemical}
        onRemove={onRemoveChemical}
        onAddRoute={onAddRoute}
        onUpdateRoute={onUpdateRoute}
        onRemoveRoute={onRemoveRoute}
      />
      <WasteSection
        records={wastes}
        disabled={disabled}
        onAdd={onAddWaste}
        onUpdate={onUpdateWaste}
        onRemove={onRemoveWaste}
        wasteTarget={process.sideStream}
        wasteTargetUnit={MASS_UNIT_MAP[process.sideStreamUnit] ?? "kg"}
      />
      <CipSipSection
        records={cipSipRecords}
        disabled={disabled}
        gateKind={gateKind}
        onAdd={onAddCip}
        onUpdate={onUpdateCip}
        onRemove={onRemoveCip}
      />
      <RecoverySection config={mainConfig} onUpdate={updateMain} disabled={disabled} />
    </div>
  );
};
